import { FC } from 'react'
import { Modal, PrimaryButton, LoadingContainer, Box } from '../../../ui'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { Alert, Divider } from 'antd'
import { useNavigate } from 'react-router-dom'
import {signBody, monthPicker, currencyFormat, isExpired} from '../../../helpers'
import { useGetTariffRate } from '../../../hooks'
import useRequest from '../../../hooks/useRequest'
import IAppState from '../../../../store/states'
import { payRealization } from '../../config'
import style from './style.m.less'
import {useCurrentUser} from "../../../hooks/useCurrentUser";
import {EUserType} from "../../../../constants/approval";

interface IPaymentModal {
    isModalVisible: boolean
    onCancel: () => void
    consumers: { user_id: string; volume: number }[]
    onOpenSubModal: () => void
    data: any
}

const PaymentModal: FC<IPaymentModal> = ({
    isModalVisible,
    onCancel,
    data,
    consumers,
    onOpenSubModal,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const userInfo = useSelector((store: IAppState) => store.user)
    const userData = userInfo.data
    const { isClient, isGOOwner, isTSO } = useCurrentUser()
    const isClientGOOwnerTSO = isClient || isGOOwner || isTSO
    const isSignExpired =  isClientGOOwnerTSO && isExpired(userData?.certificate_data.token_expiration)
    // У пользователя должна быть МЧД
    const needAttorney = userData?.user_type === EUserType.UrLico && isClientGOOwnerTSO && !userData?.without_attorney
    // У пользователя должна быть МЧД, но её нет
    const isEmptyAttorney = needAttorney && !userData?.machine_readable_attorney
    // У пользователя есть МЧД и у неё истёк срок
    const isAttorneyExpired = needAttorney
        && !!userData?.machine_readable_attorney
        && isExpired(userData?.machine_readable_attorney.validity_period, true)

    const { fetch, isLoading } = useRequest(payRealization)
    const { contract_number, contract_id, period_date, fee, seller_name, volume, buyer_uid } = data

    const { loadingTariffs, balance, balanceNumber, rate } = useGetTariffRate({
        dependency: isModalVisible,
        id: 67,
        volume,
    })

    const onConfirm = async () => {
        if (Number(balanceNumber) < fee) {
            onOpenSubModal()
            return
        }

        const body = {
            contract_number,
            period_id: contract_id,
            period_date,
            buyer_uid,
            volume,
            buyer_fee: fee,
            consumers,
        }
        const { request_id, signature } = await signBody(body, userData)

        fetch({
            body,
            getParams: {
                request_id,
            },
            config: {
                headers: {
                    Signature: signature,
                },
            },
            successMessage: {
                title: t('detailedCard.success'),
                description: 'Реализация АГ по ЗД прошла успешно',
            },
            onSuccess: () => {
                navigate(-1)
                onCancel()
            },
            errorCodeMessage: true,
        })
    }

    return (
        <LoadingContainer isLoading={loadingTariffs || isLoading}>
            <Modal isModalVisible={isModalVisible} onCancel={onCancel}>
                <div className={style.modal}>
                    <div className={style.modalWrapper}>
                        <div className={style.header}>{'Оплата комиссии'}</div>
                    </div>
                    <Box style={{ marginTop: '16px' }}>
                        <Box direction="row">
                            <div className={style.leftSide}>Номер договора</div>
                            <div className={style.rightSide}>{contract_number}</div>
                        </Box>
                        <div className={style.wrapper}>
                            <div className={style.leftSide}>Продавец (ЗГО)</div>
                            <div className={style.rightSide}>{seller_name}</div>
                        </div>
                        <div className={style.wrapper}>
                            <div className={style.leftSide}>Период поставки</div>
                            <div className={style.rightSide}>
                                {`${monthPicker(moment(period_date).month() + 1)}, ${moment(
                                    period_date,
                                ).year()}`}
                            </div>
                        </div>
                        <div className={style.wrapper}>
                            <div className={style.leftSide}>Объем</div>
                            <div className={style.rightSide}>{`${currencyFormat(
                                volume,
                            )} кВт*ч`}</div>
                        </div>
                        <Divider style={{ margin: '12px 0' }} />
                        <Box direction="row">
                            <div className={style.leftSide}>
                                <div>Комиссия за реализацию</div> АГ по ЗД
                            </div>
                            <div className={style.rightSide}>{`${currencyFormat(fee)} ₽`}</div>
                        </Box>
                    </Box>
                    <div className={style.payAlert}>
                        <div className={style.payHeader}>
                            <span className={style.alertMessage}>Сумма комиссии составила </span>
                            <span className={style.alertPrice}>{`${currencyFormat(fee)} ₽`}</span>
                        </div>
                        <div className={style.payText}>
                            <span className={style.alertDetail}>Баланс платежного счета</span>
                            <span className={style.alertDetail}>: {balance}</span>
                        </div>
                    </div>
                    {isSignExpired && (
                        <Alert
                            className={style.signError}
                            message={'Истек срок действия ЭП'}
                            type="error"
                            showIcon
                        />
                    )}
                    {(isEmptyAttorney || isAttorneyExpired) && (
                        <Alert
                            className={style.signError}
                            message={isEmptyAttorney ? t('profile.attorneyIsMissing') : t('profile.attorneyIsExpired')}
                            type="error"
                            showIcon
                        />
                    )}
                    <div className={style.buttonsBlock}>
                        <PrimaryButton
                            onClick={onCancel}
                            value="Отмена"
                            isCancel
                            customStyle={{ padding: '8px 16px' }}
                        />
                        <PrimaryButton
                            onClick={onConfirm}
                            value="Оплатить"
                            customStyle={{ marginLeft: '16px', padding: '8px 16px' }}
                            disabled={isSignExpired || isEmptyAttorney || isAttorneyExpired}
                        />
                    </div>
                </div>
            </Modal>
        </LoadingContainer>
    )
}

export default PaymentModal
